import { useRef, useState } from 'react';
import {
  AlertCircleIcon,
  CheckmarkCircle02Icon,
  CloudUploadIcon,
  Csv01Icon,
  FileDownloadIcon,
} from '@hugeicons/core-free-icons';
import { Button } from '../../components/ui/Button';
import { HugeIcon } from '../../components/ui/HugeIcon';
import { StatusPill } from '../../components/ui/StatusPill';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  Questionnaire,
  QuestionnaireDescription,
  QuestionnaireItem,
  QuestionnaireProgress,
  QuestionnaireTitle,
} from '../../components/ui/questionnaire';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import {
  importUsers,
  userImportInputSchema,
  validateUserImport,
  type Member,
  type UserImportCandidate,
  type UserImportInput,
  type UserImportValidation,
} from '../../lib/api';
import { cn } from '../../lib/cn';

const steps = ['template', 'upload', 'validate', 'preview'] as const;
type ImportStep = typeof steps[number];
const questionnaireItems = steps.map((name) => ({ name }));
const templateCsv = 'full_name,email,role\nJane Doe,jane@example.com,member\nOmar Ali,omar@example.com,org_admin\n';

export function UserImportDialog({
  importCandidates = importUsers,
  onClose,
  onImported,
  open,
  validateCandidates = validateUserImport,
}: {
  importCandidates?: (users: UserImportInput[]) => Promise<Member[]>;
  onClose: () => void;
  onImported: (members: Member[]) => void;
  open: boolean;
  validateCandidates?: (users: UserImportCandidate[]) => Promise<UserImportValidation>;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<ImportStep>('template');
  const [file, setFile] = useState<File | null>(null);
  const [candidates, setCandidates] = useState<UserImportCandidate[]>([]);
  const [validation, setValidation] = useState<UserImportValidation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const stepIndex = steps.indexOf(step);

  function resetAndClose() {
    if (busy) return;
    setStep('template');
    setFile(null);
    setCandidates([]);
    setValidation(null);
    setError(null);
    setDragActive(false);
    onClose();
  }

  async function acceptFile(nextFile: File | null) {
    setError(null);
    setValidation(null);
    setCandidates([]);
    setFile(null);
    if (!nextFile) return;
    if (!nextFile.name.toLowerCase().endsWith('.csv')) {
      setError('Choose a CSV file created from the template.');
      return;
    }
    if (nextFile.size > 1_000_000) {
      setError('The CSV file must be 1 MB or smaller.');
      return;
    }
    try {
      const parsed = parseUserCsv(await nextFile.text());
      if (parsed.length > 100) throw new Error('A single import can contain up to 100 users.');
      setCandidates(parsed);
      setFile(nextFile);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to read the CSV file.');
    }
  }

  async function advance() {
    setError(null);
    if (step === 'template') {
      setStep('upload');
      return;
    }
    if (step === 'upload') {
      if (!file || !candidates.length) {
        setError('Upload a populated CSV file to continue.');
        return;
      }
      setBusy(true);
      try {
        setValidation(await validateCandidates(candidates));
        setStep('validate');
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : 'Unable to validate the CSV file.');
      } finally {
        setBusy(false);
      }
      return;
    }
    if (step === 'validate' && validation?.failed === 0) setStep('preview');
  }

  async function confirmImport() {
    setBusy(true);
    setError(null);
    try {
      const validUsers = userImportInputSchema.array().parse(candidates);
      const imported = await importCandidates(validUsers);
      onImported(imported);
      setBusy(false);
      resetAndClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to import users.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog onOpenChange={(nextOpen) => { if (!nextOpen) resetAndClose(); }} open={open}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader className="pr-10">
          <DialogTitle className="text-xl font-bold">Import users</DialogTitle>
          <DialogDescription>Validate every user before invitations are queued.</DialogDescription>
        </DialogHeader>
        <Questionnaire
          item={step}
          items={questionnaireItems}
          onSubmit={(event) => { event.preventDefault(); if (step === 'preview') void confirmImport(); }}
        >
          <QuestionnaireProgress className="mb-4">Step {stepIndex + 1} of {steps.length}</QuestionnaireProgress>

          <QuestionnaireItem name="template">
            <QuestionnaireTitle>Download the import template</QuestionnaireTitle>
            <QuestionnaireDescription>Use the required column names and one row per user. Roles accept only member or org_admin.</QuestionnaireDescription>
            <a className="mt-5 flex w-full items-center gap-3 rounded-2xl border bg-white p-4 text-left transition hover:bg-muted/50" download="mypdf-user-import-template.csv" href={`data:text/csv;charset=utf-8,${encodeURIComponent(templateCsv)}`}>
              <span className="flex size-10 items-center justify-center rounded-xl bg-violet-100 text-violet-700"><HugeIcon icon={Csv01Icon} size={20} /></span>
              <span className="min-w-0 flex-1"><span className="block font-semibold">Download CSV template</span><span className="block text-xs text-muted-foreground">full_name, email, role</span></span>
              <HugeIcon icon={FileDownloadIcon} size={18} />
            </a>
          </QuestionnaireItem>

          <QuestionnaireItem name="upload">
            <QuestionnaireTitle>Upload the completed CSV</QuestionnaireTitle>
            <QuestionnaireDescription>Up to 100 users and 1 MB. The file is parsed in your browser before validation.</QuestionnaireDescription>
            <button
              className={cn('mt-5 flex min-h-44 w-full flex-col items-center justify-center gap-3 rounded-3xl border border-dashed bg-muted/30 p-6 text-center transition backdrop-blur-sm hover:border-sky-300 hover:bg-sky-50/50', dragActive && 'border-sky-400 bg-sky-50/70 shadow-[0_0_0_4px_rgba(14,165,233,0.10)]', file && 'border-emerald-300 bg-emerald-50/60')}
              onClick={() => inputRef.current?.click()}
              onDragEnter={(event) => { event.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => { event.preventDefault(); setDragActive(false); void acceptFile(event.dataTransfer.files[0] ?? null); }}
              type="button"
            >
              <span className={cn('flex size-11 items-center justify-center rounded-xl bg-sky-100 text-sky-700', file && 'bg-emerald-100 text-emerald-700')}><HugeIcon icon={file ? CheckmarkCircle02Icon : CloudUploadIcon} size={22} /></span>
              <span><span className="block font-semibold">{file ? file.name : 'Drop the CSV here or choose a file'}</span><span className="mt-1 block text-xs text-muted-foreground">{file ? `${candidates.length} user rows found` : 'CSV · maximum 1 MB'}</span></span>
            </button>
            <input accept=".csv,text/csv" className="sr-only" onChange={(event) => { void acceptFile(event.target.files?.[0] ?? null); event.target.value = ''; }} ref={inputRef} type="file" />
          </QuestionnaireItem>

          <QuestionnaireItem name="validate">
            <QuestionnaireTitle>Validation results</QuestionnaireTitle>
            <QuestionnaireDescription>{validation?.failed ? 'Correct the indicated rows, then upload the revised file.' : 'Every row is ready. Review the users before sending invitations.'}</QuestionnaireDescription>
            {validation ? <div className="mt-5 grid grid-cols-2 gap-3"><ResultMetric label="Ready" tone="emerald" value={validation.ready} /><ResultMetric label="Failed" tone={validation.failed ? 'rose' : 'slate'} value={validation.failed} /></div> : null}
            {validation?.failed ? (
              <div className="mt-4 max-h-64 space-y-2 overflow-y-auto rounded-2xl border bg-muted/20 p-2">
                {validation.rows.filter((row) => !row.valid).map((row) => <div className="rounded-xl bg-white p-3" key={`${row.row}-${row.email}`}><div className="flex items-center gap-2"><HugeIcon className="text-destructive" icon={AlertCircleIcon} size={16} /><p className="font-semibold">Row {row.row} · {row.email || 'Missing email'}</p></div><ul className="mt-2 space-y-1 pl-6 text-xs text-destructive">{row.errors.map((item) => <li className="list-disc" key={item}>{item}</li>)}</ul></div>)}
              </div>
            ) : validation ? <div className="mt-4 flex items-center gap-3 rounded-2xl bg-emerald-50 p-4 text-emerald-800"><HugeIcon icon={CheckmarkCircle02Icon} size={20} /><p className="text-sm font-medium">{validation.ready} {validation.ready === 1 ? 'user is' : 'users are'} ready to import.</p></div> : null}
          </QuestionnaireItem>

          <QuestionnaireItem name="preview">
            <QuestionnaireTitle>Review and confirm</QuestionnaireTitle>
            <QuestionnaireDescription>Confirming creates pending users and queues one account invitation for each row.</QuestionnaireDescription>
            <div className="mt-5 max-h-80 overflow-y-auto rounded-2xl border max-sm:bg-muted/30 max-sm:p-2">
              <Table className="min-w-[560px]">
                <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>{validation?.rows.map((row) => <TableRow key={`${row.row}-${row.email}`}><TableCell className="font-semibold">{row.fullName}</TableCell><TableCell>{row.email}</TableCell><TableCell>{row.role === 'org_admin' ? 'Organization admin' : 'Member'}</TableCell><TableCell><StatusPill status="Ready" /></TableCell></TableRow>)}</TableBody>
              </Table>
            </div>
          </QuestionnaireItem>

          {error ? <p className="mt-4 text-xs font-medium text-destructive">{error}</p> : null}
          <DialogFooter className="mt-6">
            <Button className="border-0" disabled={busy} onClick={resetAndClose} type="button" variant="destructive">Cancel</Button>
            {stepIndex > 0 ? <Button className="border-0 bg-muted/50 hover:bg-muted/70" disabled={busy} onClick={() => { setError(null); setStep(steps[stepIndex - 1]); }} type="button" variant="ghost">Previous</Button> : null}
            {step === 'validate' && validation?.failed ? <Button onClick={() => setStep('upload')} type="button"><HugeIcon icon={CloudUploadIcon} size={15} />Upload corrected CSV</Button> : step === 'preview' ? <Button disabled={busy} type="submit">{busy ? 'Importing…' : `Confirm import · ${validation?.ready ?? 0}`}</Button> : <Button disabled={busy} onClick={() => { void advance(); }} type="button">{busy ? 'Validating…' : step === 'upload' ? 'Validate file' : 'Next'}</Button>}
          </DialogFooter>
        </Questionnaire>
      </DialogContent>
    </Dialog>
  );
}

function ResultMetric({ label, tone, value }: { label: string; tone: 'emerald' | 'rose' | 'slate'; value: number }) {
  const colors = tone === 'emerald' ? 'bg-emerald-50 text-emerald-800' : tone === 'rose' ? 'bg-rose-50 text-rose-800' : 'bg-muted text-muted-foreground';
  return <div className={cn('rounded-2xl p-4', colors)}><p className="text-xs font-medium uppercase tracking-wide">{label}</p><p className="mt-2 text-2xl font-semibold">{value}</p></div>;
}

export function downloadMembersCsv(members: Member[]) {
  const rows = [
    ['full_name', 'email', 'role', 'status', 'workos_state', 'invitation_sent_at', 'invitation_expires_at'],
    ...members.map((member) => [member.fullName, member.email, member.role, member.status, member.syncState, member.invitationLastSentAt ?? '', member.invitationExpiresAt ?? '']),
  ];
  downloadCsv(rows.map((row) => row.map(escapeCsvCell).join(',')).join('\n') + '\n', `mypdf-users-${new Date().toISOString().slice(0, 10)}.csv`);
}

function downloadCsv(value: string, fileName: string) {
  const url = URL.createObjectURL(new Blob([value], { type: 'text/csv;charset=utf-8' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

function escapeCsvCell(value: string) {
  return /[",\n\r]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;
}

export function parseUserCsv(value: string): UserImportCandidate[] {
  const records = parseCsv(value);
  if (records.length < 2) throw new Error('The CSV must include a header and at least one user row.');
  const headers = records[0].map((header) => header.replace(/^\uFEFF/, '').trim().toLowerCase());
  const expected = ['full_name', 'email', 'role'];
  if (expected.some((header) => !headers.includes(header))) throw new Error('Use the template columns: full_name, email, role.');
  const indexes = Object.fromEntries(expected.map((header) => [header, headers.indexOf(header)]));
  return records.slice(1).filter((row) => row.some((cell) => cell.trim())).map((row) => ({
    fullName: row[indexes.full_name]?.trim() ?? '',
    email: row[indexes.email]?.trim() ?? '',
    role: row[indexes.role]?.trim().toLowerCase() ?? '',
  }));
}

function parseCsv(value: string) {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let index = 0; index < value.length; index += 1) {
    const character = value[index];
    if (quoted) {
      if (character === '"' && value[index + 1] === '"') { cell += '"'; index += 1; }
      else if (character === '"') quoted = false;
      else cell += character;
    } else if (character === '"' && cell.length === 0) quoted = true;
    else if (character === ',') { row.push(cell); cell = ''; }
    else if (character === '\n') { row.push(cell.replace(/\r$/, '')); rows.push(row); row = []; cell = ''; }
    else cell += character;
  }
  if (quoted) throw new Error('The CSV contains an unclosed quoted value.');
  if (cell.length || row.length) { row.push(cell.replace(/\r$/, '')); rows.push(row); }
  return rows;
}
